import { useEffect, useState } from "react";

/**
 * useIsMobile — true when the viewport is at/below `bp` (default 820px).
 * Drives the responsive shell: below the breakpoint the left IconRail collapses
 * into a top-left hamburger drawer and the Topbar sheds its search / tenant
 * chrome. Kept in its own module so both chrome.tsx and chrome-topbar.tsx can
 * import it without a circular dependency.
 */
export function useIsMobile(bp = 820): boolean {
  const q = `(max-width:${bp}px)`;
  const [m, setM] = useState(() => (typeof window !== "undefined" && "matchMedia" in window ? window.matchMedia(q).matches : false));
  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return;
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches);
    h();
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [q]);
  return m;
}

export type Viewport = "phone" | "tablet" | "desktop";

/**
 * useViewport — coarse device class for layout decisions.
 *   phone   ≤ 640px   · tablet 641–1024px · desktop > 1024px
 * The app launcher is the home surface for phone + tablet; Event Day adapts
 * its layout per class (single column → adaptive two-pane → console).
 */
export function useViewport(): Viewport {
  const phone = useIsMobile(640);
  const tabletOrLess = useIsMobile(1024);
  return phone ? "phone" : tabletOrLess ? "tablet" : "desktop";
}
